import React, { useCallback, useRef, useState } from "react";
import Header from "./Header";
import { formatFileSize, logAccess } from "../utils/utils";
import { useAuth } from "../../../features/auth/AuthContext";
import DataTable, { RowActionsTray } from "./DataTable";
import { fileIcons } from "../Icons";
import { format } from "date-fns";
import { getFileActions } from "../utils/fileActions";
import { toast } from "react-toastify";
import GridView from "./GridView";
import Loader from "./Loader";
import RenameModal from "../FileActions/RenameModal";
import DeleteModal from "../FileActions/DeleteModal";
import FilePreviewModal2 from "../../FilePreview/FilePreviewModal2";
import BreadCrumb from "./BreadCrumb";
import MoveFile from "../FileActions/MoveFile";
import Filter from "./Filter";
import Pagination from "./Pagination";
import { useFileActions } from "../hooks/useFileActions";
import { useRepoQuery } from "../hooks/useRepoQuery";
import { useModal } from "../hooks/useModal";
import useDragAndDrop from "../hooks/useDragAndDrop";
import NoFiles from "./EmptyList/NoFiles";
import DragOverlay from "./EmptyList/DragOverlay";
import {
  ALLOWED_FILE_EXTENSIONS,
  isFileTypeAllowed,
} from "../../Chatbot/services/fileUploadService";
import {
  validateFilesForUpload,
} from "../utils/fileValidation";
import api from "../../../api/api";

import { useQueryClient } from "@tanstack/react-query";
import FileUploading from "./FileUploading";
import SelectAll from "./SelectAll";
import BackButton from "../../BackButton/BackButton";
import Filelist from "./Mobile/Filelist";
import FileBulkActionBar from "./Mobile/FileBulkActionBar";
import UploadFile from "./UploadFile";
import FloatingUploadBtn from "./Mobile/FloatingUploadBtn";

const FolderView = ({
  path = [],
  setPath,
  currentFolder,
  setCurrentFolder,
  enableDragDrop = true,
}) => {
  const { token } = useAuth();
  const inputContainerRef = useRef();
  const [openFilter, setOpenFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState();
  const [activeView, setActiveView] = useState("file");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [uploadFilePopup, setUploadFilePopup] = useState(false);
  const queryClient = useQueryClient();
  const { modal, reqData, openModal, closeModal } = useModal();

  const { data, isLoading, isFetching } = useRepoQuery({
    token,
    endpoint: "manual-upload/folder-contents/",
    queryKey: "folderContents",
    folderId: currentFolder?.id,
    search: search,
    page: currentPage,
    filters,
    enabled: !openFilter,
  });

  const {
    favourite,
    download,
    openRename,
    openDelete,
    openMove,
    preview,
    folderDownload,
    deleteFile,
    isDeleting,
    isFavouriting,
  } = useFileActions({ token, openModal, closeModal });

  async function handleFileSelect(files) {
    // Validate file types first (most specific and actionable error)
    const invalidTypeFiles = files.filter((file) => !isFileTypeAllowed(file));
    if (invalidTypeFiles.length > 0) {
      const invalidNames = invalidTypeFiles.map((f) => f.name).join(", ");
      const allowedTypes = ALLOWED_FILE_EXTENSIONS.map((ext) =>
        ext.toUpperCase(),
      ).join(", ");
      toast.error(
        `Invalid file type(s): ${invalidNames}. Allowed types: ${allowedTypes}`,
      );
      return;
    }

    // Check for duplicate files (already selected/attached)
    // const existingFileNames = attachedFiles.map((f) => f.name);
    // const { uniqueFiles, duplicateFiles } = filterDuplicateFiles(
    //   files,
    //   existingFileNames,
    // );

    // Show toast error for duplicate files
    // if (duplicateFiles.length > 0) {
    //   const duplicateNames = duplicateFiles.map((f) => f.name).join(", ");
    //   toast.error(`File(s) already selected: ${duplicateNames}`);
    // }

    // If all files are duplicates, return early
    // if (uniqueFiles.length === 0) {
    //   return;
    // }

    // Continue with non-duplicate files
    // files = uniqueFiles;

    // Check existing files count
    // const currentFileCount = attachedFiles.length;
    // const remainingSlots = 3 - currentFileCount;

    if (files.length > 8) {
      toast.error(`Maximum 8 files are allowed.`);
      return;
    }
    const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);
    const totalSizeMB = newFilesSize / (1000 * 1000);

    if (totalSizeMB > 500) {
      toast.error(
        `Total file size must be less than 500 MB. Current total size: ${totalSizeMB.toFixed(
          2,
        )} MB`,
      );
      return;
    }

    const { validFiles, invalidFiles } = await validateFilesForUpload(files);

    // Show error toasts for invalid files
    if (invalidFiles.length > 0) {
      invalidFiles.forEach(({ file, reason }) => {
        toast.error(`${file.name}: ${reason}`);
      });
    }

    // If no valid files remain after validation, return early
    if (validFiles.length === 0) {
      return;
    }

    const formData = new FormData();
    validFiles.forEach((file) => {
      formData.append("files", file);
    });

    if (+currentFolder?.id !== 2 && +currentFolder?.id !== 1) {
      formData.append("parent_id", currentFolder?.id);
    }
    formData.append("type", "file");

    setUploadFiles(validFiles);
    setFileLoading(true);
    api
      .post(`/upload/`, formData, {
        headers: {
          // "Content-Type": "application/json",
          Authorization: `Token ${token}`,
        },
      })
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success("Upload Successfull.", {
            position: "top-center",
          });
          queryClient.invalidateQueries({ queryKey: ["folderContents"] });
        }
        setFileLoading(false);
        setUploadFiles();
      })
      .catch((error) => {
        toast.error("Something went wrong. Please try again later.", {
          position: "top-center",
        });
        setFileLoading(false);
        setUploadFiles();
      });
  }
  function handleInvalidPastedFileType(files) {
    const invalidNames = files.map((f) => f.name).join(", ");
    const allowedTypes = ALLOWED_FILE_EXTENSIONS.map((ext) =>
      ext.toUpperCase(),
    ).join(", ");
    toast.error(
      `Invalid file type(s): ${invalidNames}. Allowed types: ${allowedTypes}`,
    );
  }

  const { isDragging, dropZoneProps } = useDragAndDrop({
    enabled: enableDragDrop && +currentFolder?.id !== 1,
    onFilesDrop: handleFileSelect,
    onInvalidFileType: handleInvalidPastedFileType,
    dropZoneRef: inputContainerRef,
  });

  const handleFilterClick = () => {
    setOpenFilter(true);
  };

  const closeFilterDrawer = () => {
    setOpenFilter(false);
  };

  const handleDateChange = (date) => {
    setFilters((prev) => ({
      ...prev,
      start_date: date[0]?.startDate,
      end_date: date[0]?.endDate,
    }));
  };

  const resetFilters = () => {
    setFilters(null);
    setCurrentPage(1);
    closeFilterDrawer();
  };
  const handlers = {
    favourite,
    openRename,
    openDelete,
    openMove,
    download,
    folderDownload,
  };

  async function onPageChange(page) {
    setCurrentPage(page);
  }

  const folderClick = (clickedFolder) => {
    const newPath = { id: clickedFolder?.id, name: clickedFolder?.folder_name };
    setPath((prev) => [...prev, newPath]);
    setSearch("");
    setCurrentPage(1);
    setCurrentFolder(newPath);
    logAccess(token, "folder", clickedFolder?.id);
  };

  const columns = [
    {
      key: "s3_url",
      title: "Name",
      sortable: true,
      sortAccessor: (row) =>
        row?.item_type === "folder" ? row?.folder_name : row?.file_name,
      render: (val, row, idx) => {
        const FileIcon = fileIcons?.[row?.file_type] || fileIcons?.["folder"];
        return (
          <button
            // onClick={() => folderClick(row)}
            className="border-none bg-transparent flex items-center gap-2 w-full"
          >
            <span>{FileIcon && <FileIcon />}</span>
            <span className="truncate text-[#555555] font-normal text-[0.8rem]">
              {row?.item_type === "folder" ? row?.folder_name : row?.file_name}
            </span>
          </button>
        );
      },
    },
    {
      key: "upload_type",
      title: "",
      render: (val, row) => (
        <div className="file-type">
          {val ? val : row?.item_type === "folder" ? "Generated" : ""}
        </div>
      ),
    },
    {
      key: "full_path",
      title: "Location",
      align: "left",
      render: (val) => (
        <div className="truncate text-[#555555] font-normal text-[0.8rem]">
          {val.replace(/^Manually uploaded\//i, "")}
        </div>
      ),
    },
    {
      key: "updated_at",
      title: "Updated On",
      align: "left",
      sortable: true,
      type: "date",
      render: (val) => (
        <div className="truncate text-[#555555] font-normal text-[0.8rem]">
          {format(new Date(val), "dd/MM/yy")}
        </div>
      ),
    },
    {
      key: "file_size",
      title: "Size",
      align: "left",
      sortable: true,
      render: (val, row, idx) => (
        <span className="truncate text-[#555555] font-normal text-[0.8rem]">
          {formatFileSize(row?.["file_size"])}
        </span>
      ),
    },
    {
      key: "original_name",
      title: "",
      align: "left",
      render: (val, row, idx, isHovered) => {
        let rowActions = getFileActions({ row, handlers });
        return (
          <div
            style={{
              position: "relative",
              overflow: "visible",
              width: 5 * 28 + 10,
              minWidth: 5 * 28 + 10,
              // width:'100%',
              padding: "0 8px",
            }}
          >
            <RowActionsTray
              actions={rowActions}
              row={row}
              visible={isHovered}
            />
          </div>
        );
      },
    },
  ];

  function handleBreadCrumClick(folder, index) {
    const newPath = path.slice(0, index + 1);
    setPath(newPath);
    setCurrentFolder(folder);
  }

  function goBackOneFolder() {
    if (path.length <= 1) return; // Already at root, cannot go back

    // Get new path by removing the last folder
    const newPath = path.slice(0, path.length - 1);

    // Update current folder to the new last folder in path
    const newCurrentFolder = newPath[newPath.length - 1] || null;

    setPath(newPath);
    setCurrentFolder(newCurrentFolder);
  }

  const handleApplyFilter = (filterObj) => {
    setFilters(filterObj);
    setCurrentPage(1);
    closeFilterDrawer();
  };

  const handleSearchChange = useCallback(
    (input) => {
      if (input === search) return;
      setSearch(input);
      setCurrentPage(1);
    },
    [search],
  );

  const isEmptyState =
    !search?.trim() &&
    !filters &&
    !data?.all?.length &&
    !data?.ai_assistant_folder;

  const selectableRows =
    data?.files?.filter((row) => row?.upload_type === "Manual") || [];

  return (
    <div className="h-full">
      <BackButton buttonName={"Back"} onClick={goBackOneFolder} />
      <Header
        setActiveView={setActiveView}
        activeView={activeView}
        searchParam={search}
        setSearchParam={handleSearchChange}
        title={currentFolder?.name}
        fileView={true}
        // btnLabel={window.innerWidth < 640 ? null : "New"}
        clickFilter={
          currentFolder?.id !== 0 && currentFolder?.id !== 1
            ? handleFilterClick
            : false
        }
        currentFolder={currentFolder}
        searchPlaceholder="Search files or folders"
        handleDateChange={+currentFolder?.id === 1 ? handleDateChange : null}
        isAiFolder={data?.ai_assistant_folder}
        justify={window.innerWidth < 650 ? 'justify-end' : null}
      />
      {currentFolder?.id !== 0 && currentFolder?.id !== 1 && (
        <Filter
          onReset={resetFilters}
          open={openFilter}
          close={closeFilterDrawer}
          updatedOn={true}
          fileType={true}
          appliedFilters={filters}
          applyFilter={handleApplyFilter}
          showUploadType={+currentFolder?.id !== 2}
          currentFolder={currentFolder}
          enableResults
          filterAPI="manual-upload/folder-contents/"
        />
      )}
      <BreadCrumb path={path} onFolderClick={handleBreadCrumClick} />
      {isDragging && <DragOverlay currentFolder={currentFolder} />}
      {fileLoading && <FileUploading files={uploadFiles} />}

      {isLoading || isFavouriting || isFetching ? (
        <div className="h-full flex items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          {isEmptyState ? (
            <div
              ref={inputContainerRef}
              className="h-full flex items-center justify-center"
              {...dropZoneProps}
            >
              {+currentFolder?.id !== 1 && window.innerWidth > 768 ? (
                <NoFiles isDrag={true} />
              ) : (
                <NoFiles />
              )}
            </div>
          ) : (
            <div
              ref={inputContainerRef}
              {...(!data?.ai_assistant_folder ? dropZoneProps : {})}
              className="md:shadow-[0_0_12px_#00000014] px-2 md:px-8 py-2 bg-white rounded-[12px] mt-4 relative"
            >
              {activeView === "file" ? (
                <>
                  {window.innerWidth < 640 ? (
                    <div className="flex flex-col gap-4">
                      {selectedKeys?.length > 0 && (
                        <FileBulkActionBar
                          data={data?.all}
                          handleDelete={openDelete}
                          selectedKeys={selectedKeys}
                          setSelectedKeys={setSelectedKeys}
                          handleFav={favourite}
                        />
                      )}
                      <Filelist
                        data={data?.all}
                        folderClick={folderClick}
                        filePreview={preview}
                        handleMove={openMove}
                        handleRename={openRename}
                        handleDelete={openDelete}
                        currentFolder={currentFolder}
                        changeFav={favourite}
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                      />
                    </div>
                  ) : (
                    <>
                      <SelectAll
                        selectableRows={selectableRows}
                        handleFav={favourite}
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                        handleDelete={openDelete}
                      />

                      <DataTable
                        columns={columns}
                        dataSource={data?.all}
                        rowKey="key"
                        maxHeight="max-h-90vh"
                        // parentWidth="max-w-90vw"
                        selectedKeys={selectedKeys}
                        setSelectedKeys={setSelectedKeys}
                        checkable={true}
                        rowSelectable={(row) =>
                          row?.upload_type === "Manual" || false
                        }
                        onRow={(row) => ({
                          onClick: () => {
                            if (row?.item_type === "folder") {
                              // if(!isLoading)
                              folderClick?.(row);
                            } else {
                              preview(row);
                            }
                          },
                        })}
                        // emptyText="No files found"
                      />
                    </>
                  )}
                </>
              ) : (
                <GridView
                  handleMove={openMove}
                  handleRename={openRename}
                  handleDelete={openDelete}
                  currentFolder={currentFolder}
                  changeFav={favourite}
                  files={data?.files}
                  totalFiles={data?.totalFiles}
                  totalFolders={data?.totalFolders}
                  folders={data?.folders}
                  previewFile={preview}
                  folderClickFromView={folderClick}
                  selectedKeys={selectedKeys}
                  setSelectedKeys={setSelectedKeys}
                />
              )}
            </div>
          )}
        </>
      )}
      <div className="py-3">
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalCount={data?.count || 12}
        />
      </div>
      <RenameModal
        open={modal === "rename"}
        close={closeModal}
        file={reqData}
      />
      <DeleteModal
        open={modal === "delete"}
        close={closeModal}
        loading={isDeleting}
        handleDelete={() =>
          deleteFile(reqData || selectedKeys, "soft", setSelectedKeys)
        }
        fileName={reqData?.file_name || null}
        selectedKeys={selectedKeys}
      />
      <FilePreviewModal2
        isOpen={modal === "filePreview"}
        file={reqData}
        onClose={closeModal}
      />
      <MoveFile open={modal === "moveFile"} file={reqData} close={closeModal} />
      {window.innerWidth < 640 && +currentFolder?.id !== 1 && (
        <>
          <FloatingUploadBtn onClick={() => setUploadFilePopup(true)} />
          <UploadFile
            setFileUploading={setFileLoading}
            showUploadPop={uploadFilePopup}
            setShowUploadPop={setUploadFilePopup}
            currentFolder={currentFolder}
            setFiles={setUploadFiles}
          />
        </>
      )}
    </div>
  );
};

export default FolderView;
